// src/components/dashboard/TicketsDashboard.jsx
import { useState, useEffect, useCallback } from 'react';
import styles from './TicketsDashboard.module.css';
import TicketsTable from './TicketsTable';
import CreateRequestCards from './CreateRequestCards';
import { useTickets, useClosedTickets } from '../../hooks/useTickets';
import { safeLocalStorageGet } from '../../utils/safeJsonParse';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

/** ID of the request-cards section used for smooth-scroll targeting. */
export const CARDS_SECTION_ID = 'request-cards-section';

/** ID of the tickets section used for back-to-top smooth-scroll targeting. */
export const TICKETS_SECTION_ID = 'my-tickets-section';

/** Tab identifiers */
const TAB_MY_TICKETS = 'my-tickets';
const TAB_CLOSED = 'closed';

// ─────────────────────────────────────────────────────────
// Scroll helpers (pure — easy to unit-test)
// ─────────────────────────────────────────────────────────

/**
 * Smoothly scrolls to an element by its DOM id.
 * No-ops silently when the element is not found.
 *
 * @param {string} id - The element's `id` attribute value.
 */
export function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

// ─────────────────────────────────────────────────────────
// Icon components
// ─────────────────────────────────────────────────────────

/**
 * Clipboard/ticket icon used beside the "My Tickets" heading.
 */
export const TicketIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

/**
 * Plus icon used in the "+ Create Ticket" button.
 */
export const PlusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/**
 * Circular refresh/reload icon.
 */
export const RefreshIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

/**
 * Upward chevron / arrow icon used in the "Back to top" button.
 */
export const ChevronUpIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

/**
 * Checkmark circle icon for the "Closed Tickets" tab.
 */
export const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────

/**
 * Animated ticket count pill.
 *
 * @param {{ count: number|null, loading: boolean }} props
 */
export function CountPill({ count, loading }) {
  if (loading) return <span className={styles.countPillLoading} aria-label="Loading count" />;
  if (count == null || count === 0) return null;
  return (
    <span className={styles.countPill} aria-label={`${count} tickets`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * Section header row — tab bar, count pill, Refresh button,
 * and the "+ Create Ticket" button, all in one row.
 *
 * @param {{ activeTab: string, openCount: number, closedCount: number, loading: boolean, onTabChange: Function, onRefresh: Function, onCreateTicket: Function }} props
 */
export function TicketsHeader({ activeTab, openCount, closedCount, loading, onTabChange, onRefresh, onCreateTicket }) {
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitle}>
        {/* ── Tab bar ── */}
        <div className={styles.tabBar} role="tablist" aria-label="Ticket tabs">
          <button
            className={`${styles.tab} ${activeTab === TAB_MY_TICKETS ? styles.tabActive : ''}`}
            onClick={() => onTabChange(TAB_MY_TICKETS)}
            role="tab"
            aria-selected={activeTab === TAB_MY_TICKETS}
            data-testid="tab-my-tickets"
          >
            <TicketIcon />
            <span>My Tickets</span>
            <CountPill count={openCount} loading={loading} />
          </button>

          <button
            className={`${styles.tab} ${activeTab === TAB_CLOSED ? styles.tabActive : ''}`}
            onClick={() => onTabChange(TAB_CLOSED)}
            role="tab"
            aria-selected={activeTab === TAB_CLOSED}
            data-testid="tab-closed-tickets"
          >
            <CheckCircleIcon />
            <span>Closed Tickets</span>
            <CountPill count={closedCount} loading={loading} />
          </button>
        </div>
      </div>

      <div className={styles.headerActions}>
        {/* Refresh */}
        <button
          className={`${styles.refreshBtn} ${loading ? styles.refreshBtnSpinning : ''}`}
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh ticket list"
          title="Refresh ticket list"
          data-testid="refresh-button"
        >
          <RefreshIcon />
          <span>Refresh</span>
        </button>

        {/* + Create Ticket */}
        <button
          className={styles.createTicketBtn}
          onClick={onCreateTicket}
          data-testid="create-ticket-button"
          aria-label="Create a new ticket"
        >
          <PlusIcon />
          <span>Create Ticket</span>
        </button>
      </div>
    </div>
  );
}

/**
 * "Back to top" button rendered below the request cards.
 * Smooth-scrolls back to the My Tickets section when clicked.
 *
 * @param {{ onClick: Function }} props
 */
export function BackToTopButton({ onClick }) {
  return (
    <div className={styles.backToTopRow}>
      <button
        className={styles.backToTopBtn}
        onClick={onClick}
        data-testid="back-to-top-button"
        aria-label="Back to top"
      >
        <ChevronUpIcon />
        <span>Back to top</span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────

/**
 * Staff support dashboard.
 *
 * Layout (all sections always visible — no tab switching):
 *   1. Tab bar (My Tickets / Closed Tickets) + count + [Refresh] [+ Create Ticket] buttons
 *   2. Tickets table (switches based on active tab)
 *   3. Request-type cards grid (always visible below the table)
 *   4. ↑ Back to Top button
 *
 * Auto-refreshes the ticket list when:
 *   - The browser tab regains visibility (user returns from a form page).
 *   - A form sets `pending_ticket_refresh` in localStorage after submission.
 *   - The user clicks the manual Refresh button.
 *
 * @param {{ userName: string }} props
 */
export default function TicketsDashboard({ userName = '' }) {
  const profile = safeLocalStorageGet('staff_profile', null);
  const email = profile?.primaryEmail ?? profile?.email ?? profile?.username ?? '';

  const [activeTab, setActiveTab] = useState(TAB_MY_TICKETS);

  const { tickets: openTickets, loading: openLoading, error: openError, refetch: refetchOpen } = useTickets(email);
  const { tickets: closedTickets, loading: closedLoading, error: closedError, refetch: refetchClosed } = useClosedTickets(email);

  // Derive current tab's data
  const isMyTickets = activeTab === TAB_MY_TICKETS;
  const tickets = isMyTickets ? openTickets : closedTickets;
  const loading = isMyTickets ? openLoading : closedLoading;
  const error = isMyTickets ? openError : closedError;

  /** Refetch the active tab's data. */
  const refetch = useCallback(() => {
    if (isMyTickets) {
      refetchOpen();
    } else {
      refetchClosed();
    }
  }, [isMyTickets, refetchOpen, refetchClosed]);

  /**
   * Reads the `pending_ticket_refresh` localStorage flag set by form
   * submissions and triggers a refetch when found, then clears the flag.
   */
  const checkAndRefresh = useCallback(() => {
    try {
      if (localStorage.getItem('pending_ticket_refresh') === 'true') {
        localStorage.removeItem('pending_ticket_refresh');
        refetchOpen();
      }
    } catch {
      // localStorage may be unavailable — non-fatal
    }
  }, [refetchOpen]);

  /** Registers a visibilitychange listener so the list refreshes
   *  when the user navigates back from a form page. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkAndRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    checkAndRefresh(); // also check immediately on mount
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [checkAndRefresh]);

  /** Smooth-scrolls down to the request-cards section. */
  const handleCreateClick = () => scrollToId(CARDS_SECTION_ID);

  /** Smooth-scrolls back up to the My Tickets section. */
  const handleBackToTop = () => scrollToId(TICKETS_SECTION_ID);

  return (
    <section className={styles.dashboard} aria-label="Support dashboard">

      {/* ── Tickets header with tab bar ── */}
      <div id={TICKETS_SECTION_ID}>
        <TicketsHeader
          activeTab={activeTab}
          openCount={openTickets.length}
          closedCount={closedTickets.length}
          loading={openLoading || closedLoading}
          onTabChange={setActiveTab}
          onRefresh={refetch}
          onCreateTicket={handleCreateClick}
        />
      </div>

      {/* ── Tickets table ── */}
      <div className={styles.tableSection}>
        <TicketsTable
          tickets={tickets}
          loading={loading}
          error={error}
          emptyMessage={isMyTickets
            ? undefined
            : 'No closed tickets found.'
          }
        />
      </div>

      {/* ── Request cards (always visible) ── */}
      <div id={CARDS_SECTION_ID} className={styles.cardsSection}>
        <CreateRequestCards />
      </div>

      {/* ── Back to top ── */}
      <BackToTopButton onClick={handleBackToTop} />

    </section>
  );
}
